import React, { useState } from "react";
import {
  sdh_applyDecisions, SME_ASSESSMENTS, GATE_TONE,
  v2Status, acceptPrompt, previousTitle, titleChanged,
  type EnrichedRule, type AssessmentDef,
} from "../api/sdhAPI_v2";
import { measurementOf, NotMeasured, impactText } from "../dq/measurement";
import SampleRecordsPanel from "../dq/SampleRecordsPanel";
import FieldValuesPanel from "../dq/FieldValuesPanel";
import RuleVersionsPanel from "../dq/RuleVersionsPanel";
import RuleStudioPanel from "../dq/RuleStudioPanel";
import { useAuth } from "../hooks/useAuth";

/**
 * RuleDetailV2 — everything about one rule, in the order she needs it.
 *
 *   1. Her decision, and — if the rule changed since — ONE question to settle it.
 *   2. What it enforces, and what it does to the data.
 *   3. Evidence, on demand: sample records, field values, version history.
 *
 * The accept prompt is the point of this screen. A CONDITIONAL_* category already states WHAT had to
 * happen ("pending governed values"), so when the agent revises the rule we ask whether that condition
 * is now met, with the likely answer pre-selected — instead of silently inheriting a verdict that was
 * made about an older version.
 */
export default function RuleDetailV2(
  { entityId, appNm, rule, defs, onClose, onChanged }:
  { entityId: string; appNm: string; rule: EnrichedRule; defs?: AssessmentDef[];
    onClose: () => void; onChanged: (m: string) => void }
) {
  const { user } = useAuth();
  const all = defs && defs.length ? defs : SME_ASSESSMENTS;
  const s = v2Status(rule);
  const m = measurementOf(rule);
  const prompt = acceptPrompt(rule);
  const raw: any = (rule as any)._raw || {};

  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState<string>(s.comment || "");
  const [showSamples, setShowSamples] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showStudio, setShowStudio] = useState(false);
  const [fieldFor, setFieldFor] = useState<{ table: string; column: string } | null>(null);

  const save = async (assessment: string, msg: string) => {
    const def = all.find((d) => d.value === assessment);
    if (!comment.trim()) { onChanged("Add a short comment first."); return; }
    setBusy(true);
    try {
      await sdh_applyDecisions(entityId, appNm, [{
        constraint_id: rule.rule_id, decision_type: "rule_review",
        choice: def?.choice || "reject_rule", assessment, decision_gate: def?.gate,
        comment: comment.trim(),
        sme_id: user?.user_id || user?.email || "unknown",
        sme_name: user?.full_name || user?.email,
      }]);
      onChanged(msg);
    } catch { onChanged("Could not save."); }
    finally { setBusy(false); }
  };

  const fails = raw.impact_assessment?.sample_fail_records || [];
  const passes = raw.impact_assessment?.sample_pass_records || [];
  const ruleFields = (raw.participating_fields || []).map((p: any) => p.field_id).filter(Boolean);
  const versions = (rule as any).revisions?.length || 0;
  const prev = previousTitle(rule);

  return (
    <div className="rd-scrim" onClick={onClose}>
      <div className="rd" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <style>{CSS}</style>
        <header>
          <div>
            <div className="eyebrow">RULE</div>
            <div className="title">{rule.rule_name || rule.rule_id}</div>
            {s.awaitingConfirmation && prev && titleChanged(rule) &&
              <div className="was">was “{prev}” before the agent's revision</div>}
            <div className="fid">{(rule as any).owner_field || raw.owner_field_id}
              {(rule as any).version ? ` · v${(rule as any).version}` : ""}</div>
          </div>
          <button className="x" onClick={onClose}>×</button>
        </header>

        <div className="rd-body">
          {/* 1 — her decision, and the one question if the rule moved on */}
          {prompt ? (
            <section className="ask">
              <div className="k">✎ This rule changed after your review</div>
              <p>{prompt.question}</p>
              <textarea rows={2} value={comment} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
                placeholder="Update your comment (kept with this version)" />
              <div className="acts">
                <button className="btn go" disabled={busy}
                  onClick={() => save("APPROVED_AS_IS", "Confirmed — approved at this version.")}>
                  ✓ Yes — approve
                </button>
                <button className="btn ghost" disabled={busy}
                  onClick={() => save((rule as any).sme_decision?.assessment || "CONDITIONAL_CORRECTION",
                                      "Kept as conditional at this version.")}>
                  Still conditional
                </button>
                <button className="btn rej" disabled={busy}
                  onClick={() => save("REJECTED_INVALID", "Rejected at this version.")}>Reject</button>
              </div>
            </section>
          ) : s.comment ? (
            <section className="yours" style={{ ["--t" as any]: s.tone }}>
              <div className="hd"><span className="g" style={{ background: s.tone }}>{s.label}</span>
                <span className="a">{s.assessmentLabel}</span>
                <span className="w">{s.by}{s.at ? ` · ${s.at}` : ""}{s.reviewedVersion ? ` · reviewed at v${s.reviewedVersion}` : ""}</span></div>
              <p className="c">“{s.comment}”</p>
            </section>
          ) : null}

          {/* 2 — what it enforces and what it does */}
          <section>
            <div className="k">What it enforces</div>
            <p className="p">{(rule as any).business_logic || (rule as any).description || "—"}</p>
            {raw.predicate_template && <pre className="code">{raw.predicate_template}</pre>}
          </section>

          <section>
            <div className="k">On your data</div>
            {m.ok ? (
              <div className="kpis">
                <div><span>{(m.applicable ?? 0).toLocaleString()}</span><small>records checked</small></div>
                <div><span className={m.violations ? "r" : "g"}>{(m.violations ?? 0).toLocaleString()}</span><small>flagged</small></div>
                <div><span className={m.violations ? "r" : "g"}>{m.ratePct}%</span><small>violation rate</small></div>
              </div>
            ) : <NotMeasured m={m} />}
            <div className="row">
              {(fails.length || passes.length) ?
                <button className="lnk" onClick={() => setShowSamples(true)}>
                  ⌕ Inspect {fails.length} failing / {passes.length} passing record(s)</button> : null}
              {ruleFields.map((f: string) => {
                const [t, c] = String(f).split("::");
                return t && c ? <button key={f} className="lnk" onClick={() => setFieldFor({ table: t, column: c })}>
                  ⌕ values in {c}</button> : null;
              })}
            </div>
          </section>

          {/* 3 — history: ONE way in */}
          <section className="hist">
            <div className="k">History</div>
            <div className="row">
              <span className="mut">Version {(rule as any).version ?? 1}{versions > 1 ? ` of ${versions}` : ""}</span>
              {versions > 1 && <button className="lnk" onClick={() => setShowVersions(true)}>
                view all versions &amp; switch active</button>}
              <button className="lnk" onClick={() => setShowStudio(true)}>✨ Refine with agent</button>
            </div>
          </section>
        </div>

        <footer>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </footer>
      </div>

      {showSamples && (
        <SampleRecordsPanel title={rule.rule_name || rule.rule_id}
          subtitle={(rule as any).owner_field || raw.owner_field_id}
          fails={fails} passes={passes} ruleFields={ruleFields}
          onClose={() => setShowSamples(false)} />
      )}
      {fieldFor && (
        <FieldValuesPanel entityId={entityId} table={fieldFor.table} column={fieldFor.column}
          onClose={() => setFieldFor(null)} />
      )}
      {showVersions && (
        <RuleVersionsPanel entityId={entityId} appNm={appNm} constraintId={rule.rule_id}
          ruleName={rule.rule_name} onClose={() => setShowVersions(false)}
          onChanged={(msg) => { setShowVersions(false); onChanged(msg); }} />
      )}
      {showStudio && (
        <RuleStudioPanel entityId={entityId} appNm={appNm} rule={rule}
          onClose={() => setShowStudio(false)}
          onSaved={(msg) => { setShowStudio(false); onChanged(msg); }} />
      )}
    </div>
  );
}

const CSS = `
.rd-scrim{position:fixed;inset:0;z-index:86;background:rgba(14,36,54,.4);display:flex;align-items:center;justify-content:center;padding:24px}
.rd{--line:#e3e8ee;--muted:#6b7c8d;width:min(860px,96vw);max-height:92vh;background:#fff;border-radius:16px;
 display:flex;flex-direction:column;overflow:hidden;box-shadow:0 28px 70px rgba(0,0,0,.3);
 font-family:system-ui,-apple-system,sans-serif;color:#0E2436}
.rd header{display:flex;align-items:flex-start;justify-content:space-between;padding:15px 20px;background:#0e2436;color:#fff}
.rd .eyebrow{font-size:10px;letter-spacing:.12em;color:#9db4c9}
.rd .title{font-size:16px;font-weight:700;font-family:'Space Grotesk',system-ui,sans-serif}
.rd .was{font-size:11.5px;color:#c9b892;font-style:italic;margin-top:2px}
.rd .fid{font-size:11.5px;color:#9db4c9;font-family:ui-monospace,monospace;margin-top:2px}
.rd .x{all:unset;cursor:pointer;font-size:24px;color:#cdd8ea}.rd .x:hover{color:#fff}
.rd-body{overflow:auto;padding:16px 20px}
.rd section{margin-bottom:18px}
.rd .k{font-size:10.5px;letter-spacing:.07em;text-transform:uppercase;color:#9aa6b2;font-weight:700;margin-bottom:6px}
.rd .p{font-size:13.5px;line-height:1.6;color:#33404e;margin:0}
.rd .code{background:#0e2436;color:#dbe7f0;border-radius:9px;padding:11px 13px;font-size:12px;
 font-family:ui-monospace,monospace;overflow-x:auto;margin:8px 0 0;white-space:pre-wrap}
.ask{background:#f6f9fb;border:1px solid #cfe0ea;border-radius:11px;padding:13px 15px}
.ask .k{color:#2c5b7a}
.ask p{font-size:13.5px;line-height:1.6;margin:0 0 9px;color:#22364a}
.ask textarea{width:100%;box-sizing:border-box;font:inherit;font-size:13px;padding:8px 11px;
 border:1px solid var(--line);border-radius:9px;resize:vertical}
.ask .acts{display:flex;gap:9px;margin-top:10px;flex-wrap:wrap}
.yours{border-left:3px solid var(--t);background:#f8fafb;border-radius:9px;padding:10px 13px}
.yours .hd{display:flex;align-items:center;gap:9px;flex-wrap:wrap}
.yours .g{font-size:10px;font-weight:700;color:#fff;border-radius:20px;padding:2px 9px}
.yours .a{font-size:13px;font-weight:600}
.yours .w{margin-left:auto;font-size:11.5px;color:var(--muted);font-family:ui-monospace,monospace}
.yours .c{font-size:13px;font-style:italic;color:#33404e;margin:6px 0 0;line-height:1.55}
.kpis{display:flex;gap:26px}
.kpis span{display:block;font-family:ui-monospace,monospace;font-size:21px;font-weight:600}
.kpis .r{color:#b23a30}.kpis .g{color:#2f7a4f}
.kpis small{font-size:11px;color:var(--muted)}
.rd .row{display:flex;gap:12px;align-items:center;flex-wrap:wrap;margin-top:9px}
.rd .mut{font-size:12.5px;color:var(--muted)}
.lnk{all:unset;cursor:pointer;font-size:12.5px;color:#0e7c7b}
.lnk:hover{text-decoration:underline}
.rd footer{display:flex;justify-content:flex-end;padding:12px 20px;border-top:1px solid var(--line);background:#f8fafb}
.btn{all:unset;cursor:pointer;padding:8px 16px;border-radius:9px;font-size:13px;font-weight:600}
.btn.go{background:#2f7a4f;color:#fff}.btn.ghost{background:#eef1f5;color:#42586e}
.btn.rej{background:#fdeceb;color:#b23a30}
.btn[disabled]{opacity:.5;cursor:not-allowed}
`;
